import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  listCompanies,
  createCompany,
  updateCompany,
  deleteCompany,
  toggleCompanyStatus,
  getCompanyStats
} from '../actions/companyActions';
import {
  COMPANY_CREATE_RESET,
  COMPANY_UPDATE_RESET
} from '../constants/companyConstants';

const CompanyMaster = () => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filterStatus, setFilterStatus] = useState('true');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCompany, setCurrentCompany] = useState({
    companyName: '',
    shortName: '',
    directorName: '',
    accountNumber: '',
    bankName: '',
    ifscCode: '',
    branchName: '',
    gstNumber: '',
    panNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    email: '',
    phone: '',
    alternatePhone: '',
    website: '',
    registrationNumber: '',
    notes: ''
  });

  const companyList = useSelector((state) => state.companyList);
  const { loading, error, companies, currentPage, totalPages, totalRecords } = companyList;

  const companyCreate = useSelector((state) => state.companyCreate);
  const { loading: createLoading, success: createSuccess, error: createError } = companyCreate;

  const companyUpdate = useSelector((state) => state.companyUpdate);
  const { loading: updateLoading, success: updateSuccess, error: updateError } = companyUpdate;

  const companyDelete = useSelector((state) => state.companyDelete);
  const { success: deleteSuccess } = companyDelete;

  const companyToggleStatus = useSelector((state) => state.companyToggleStatus);
  const { success: toggleSuccess } = companyToggleStatus;

  const companyStats = useSelector((state) => state.companyStats);
  const { loading: statsLoading, stats } = companyStats;

  useEffect(() => {
    dispatch(listCompanies(searchTerm, page + 1, rowsPerPage, filterStatus));
    dispatch(getCompanyStats());
  }, [dispatch, page, rowsPerPage, filterStatus, deleteSuccess, toggleSuccess]);

  useEffect(() => {
    if (createSuccess) {
      toast.success('Company created successfully');
      handleCloseDialog();
      dispatch({ type: COMPANY_CREATE_RESET });
      dispatch(listCompanies(searchTerm, page + 1, rowsPerPage, filterStatus));
      dispatch(getCompanyStats());
    }
    if (createError) {
      toast.error(createError);
    }
  }, [createSuccess, createError, dispatch, searchTerm, page, rowsPerPage, filterStatus]);

  useEffect(() => {
    if (updateSuccess) {
      toast.success('Company updated successfully');
      handleCloseDialog();
      dispatch({ type: COMPANY_UPDATE_RESET });
      dispatch(listCompanies(searchTerm, page + 1, rowsPerPage, filterStatus));
    }
    if (updateError) {
      toast.error(updateError);
    }
  }, [updateSuccess, updateError, dispatch, searchTerm, page, rowsPerPage, filterStatus]);

  const handleSearch = () => {
    setPage(0);
    dispatch(listCompanies(searchTerm, 1, rowsPerPage, filterStatus));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = () => {
    setEditMode(false);
    setCurrentCompany({
      companyName: '',
      shortName: '',
      directorName: '',
      accountNumber: '',
      bankName: '',
      ifscCode: '',
      branchName: '',
      gstNumber: '',
      panNumber: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      email: '',
      phone: '',
      alternatePhone: '',
      website: '',
      registrationNumber: '',
      notes: ''
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (company) => {
    setEditMode(true);
    setCurrentCompany(company);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCompany({
      companyName: '',
      shortName: '',
      directorName: '',
      accountNumber: '',
      bankName: '',
      ifscCode: '',
      branchName: '',
      gstNumber: '',
      panNumber: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      email: '',
      phone: '',
      alternatePhone: '',
      website: '',
      registrationNumber: '',
      notes: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCompany({ ...currentCompany, [name]: value });
  };

  const handleSubmit = () => {
    if (!currentCompany.companyName || !currentCompany.directorName || !currentCompany.accountNumber) {
      toast.error('Company name, director name, and account number are required');
      return;
    }

    if (editMode) {
      dispatch(updateCompany(currentCompany._id, currentCompany));
    } else {
      dispatch(createCompany(currentCompany));
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      dispatch(deleteCompany(id));
      toast.success('Company deleted successfully');
    }
  };

  const handleToggleStatus = (id) => {
    dispatch(toggleCompanyStatus(id));
    toast.success('Company status updated successfully');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BusinessIcon sx={{ mr: 2, fontSize: 40 }} />
        Company Master
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Companies
              </Typography>
              <Typography variant="h4">
                {statsLoading ? <CircularProgress size={24} /> : stats?.totalCompanies || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Companies
              </Typography>
              <Typography variant="h4" color="success.main">
                {statsLoading ? <CircularProgress size={24} /> : stats?.activeCompanies || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Inactive Companies
              </Typography>
              <Typography variant="h4" color="error.main">
                {statsLoading ? <CircularProgress size={24} /> : stats?.inactiveCompanies || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        {/* Search and Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Search by Company Name, Director, or Account Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={filterStatus}
                label="Status Filter"
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{ height: '56px' }}
            >
              Add Company
            </Button>
          </Grid>
        </Grid>

        {/* Companies Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Director Name</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell>Bank Name</TableCell>
                    <TableCell>IFSC Code</TableCell>
                    <TableCell>GST Number</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies && companies.length > 0 ? (
                    companies.map((company) => (
                      <TableRow key={company._id}>
                        <TableCell>{company.companyName}</TableCell>
                        <TableCell>{company.directorName}</TableCell>
                        <TableCell>{company.accountNumber}</TableCell>
                        <TableCell>{company.bankName || '-'}</TableCell>
                        <TableCell>{company.ifscCode || '-'}</TableCell>
                        <TableCell>{company.gstNumber || '-'}</TableCell>
                        <TableCell>{company.phone || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            icon={company.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                            label={company.isActive ? 'Active' : 'Inactive'}
                            color={company.isActive ? 'success' : 'error'}
                            size="small"
                            onClick={() => handleToggleStatus(company._id)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenEditDialog(company)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(company._id)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No companies found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRecords || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Company' : 'Add New Company'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Company Name"
                name="companyName"
                value={currentCompany.companyName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Short Name (e.g. MSER, MS)"
                name="shortName"
                value={currentCompany.shortName || ''}
                onChange={handleInputChange}
                helperText="Used as CUSTOM_DETAILS1 in Excel bank sheet export"
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Director Name"
                name="directorName"
                value={currentCompany.directorName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Account Number"
                name="accountNumber"
                value={currentCompany.accountNumber}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bankName"
                value={currentCompany.bankName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IFSC Code"
                name="ifscCode"
                value={currentCompany.ifscCode}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Branch Name"
                name="branchName"
                value={currentCompany.branchName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GST Number"
                name="gstNumber"
                value={currentCompany.gstNumber}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PAN Number"
                name="panNumber"
                value={currentCompany.panNumber}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration Number"
                name="registrationNumber"
                value={currentCompany.registrationNumber}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={currentCompany.phone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Alternate Phone"
                name="alternatePhone"
                value={currentCompany.alternatePhone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={currentCompany.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={currentCompany.website}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={currentCompany.address}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={currentCompany.city}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                name="state"
                value={currentCompany.state}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pincode"
                name="pincode"
                value={currentCompany.pincode}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={currentCompany.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={createLoading || updateLoading}
          >
            {createLoading || updateLoading ? <CircularProgress size={24} /> : editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompanyMaster;
